/**
 * App-mode detection: which mobile shell is currently hosting this React
 * bundle? Set at boot time via `?app=driver` on the initial URL that the
 * native shell loads (see `capacitor.driver.config.ts`), or via localStorage
 * for web override during development.
 *
 * Values:
 *   "passenger" — Drivon consumer app (default for web + iOS/Android passenger build)
 *   "driver"    — Drivon Driver app
 *
 * Usage:
 *   import { getAppMode, isDriverApp } from "../lib/app-mode";
 *   if (isDriverApp()) { ... }
 */
const STORAGE_KEY = "drivon_app_mode";

function readFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const m = params.get("app");
    if (m === "driver" || m === "passenger") return m;
  } catch { /* SSR / test env */ }
  return null;
}

function readFromStorage() {
  try { return localStorage.getItem(STORAGE_KEY); }
  catch { return null; }
}

function writeToStorage(mode) {
  try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* ignore */ }
}

/**
 * Returns "passenger" | "driver". First call captures & caches the mode from
 * the URL query string so subsequent navigation inside the SPA keeps the
 * same identity.
 */
export function getAppMode() {
  const fromUrl = readFromUrl();
  if (fromUrl) {
    writeToStorage(fromUrl);
    return fromUrl;
  }
  const fromStorage = readFromStorage();
  if (fromStorage === "driver" || fromStorage === "passenger") return fromStorage;
  return "passenger";
}

export function isDriverApp() {
  return getAppMode() === "driver";
}

export function isPassengerApp() {
  return getAppMode() === "passenger";
}

/** Manually flip the app mode (used by the web preview "Try driver mode" button). */
export function setAppMode(mode) {
  writeToStorage(mode);
  window.dispatchEvent(new CustomEvent("drivon:app-mode-changed", { detail: { mode } }));
}
